import type { Signal, WritableSignal } from '@angular/core';
import { computed, inject, Injectable, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';

import type { AppIdentity, Organization } from '@evc/platform';
import { OrganizationsService } from '@evc/platform';
import type { BadgeItemType, Menu, MenuItemType } from '@evc/web-components';
import { BtnActionEnum, ThemeService } from '@evc/web-components';
import { I18nService } from '@shared/services/i18n/i18n.service';
import { VERSION } from '@shared/version/version';

@Injectable({
  providedIn: 'root',
})
export class NavConfigService {
  readonly #router = inject(Router);
  #i18nService = inject(I18nService);
  #themeService = inject(ThemeService);
  #organizationService = inject(OrganizationsService);

  #organizations!: WritableSignal<Organization[]>;
  #leftbarConfig!: WritableSignal<Menu[]>;
  #appIdentity!: Signal<AppIdentity>;
  #helpMenu!: WritableSignal<MenuItemType[]>;

  constructor() {
    this.initConfig();

    this.#router.events.pipe(takeUntilDestroyed()).subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.updateSelectedMenuItem();
      }
    });
  }

  private initConfig(): void {
    this.#organizations = this.#organizationService.entries as WritableSignal<Organization[]>;

    this.#leftbarConfig = signal<Menu[]>([
      {
        title: this.#i18nService.t$('navigation.settings'),
        items: [
          {
            text: this.#i18nService.t$('PAGE_PROFILE.title'),
            key: '/',
            icon: 'user',
            selected: false,
            action: {
              type: BtnActionEnum.CALLBACK,
              callbackOrUrl: () => this.#router.navigateByUrl('/'),
            },
          },
          {
            text: this.#i18nService.t$('PAGE_SECURITY.title'),
            key: '/security-privacy',
            icon: 'analyze',
            selected: false,
            action: {
              type: BtnActionEnum.CALLBACK,
              callbackOrUrl: () => this.#router.navigateByUrl('/security-privacy'),
            },
          },
        ],
      },
      {
        title: this.#i18nService.t$('navigation.organization'),
        badgeItems: computed(() => {
          const currentOrg = this.#organizations().find((org) => org.current);

          return currentOrg ? [currentOrg] : [];
        }) as Signal<BadgeItemType[]>,
      },
    ]);

    this.#appIdentity = computed<AppIdentity>(() => {
      const theme = this.#themeService.resolvedTheme();
      const title = this.#i18nService.tSignal('title')();
      const logoSuffix = theme === 'dark' ? '-dark' : '';
      return {
        title: title,
        logo: `/assets/logo${logoSuffix}.svg`,
        version: VERSION.semver,
      };
    });

    this.#helpMenu = signal<MenuItemType[]>(
      ['help_and_support', 'release_notes', 'terms_of_use', 'platform_status'].map((key) => ({
        key,
        text: this.#i18nService.t$(`links.${key}.text`),
        action: {
          type: BtnActionEnum.URL,
          callbackOrUrl: this.#i18nService.t$(`links.${key}.href`),
        },
      })) as MenuItemType[],
    );
  }

  public updateSelectedMenuItem(): void {
    if (!this.#leftbarConfig) return;

    const currentUrl = this.#router.url;

    this.#leftbarConfig.update((config) =>
      config.map((section) => ({
        ...section,
        items: section.items?.map((item) => ({
          ...item,
          selected: item.key === currentUrl,
        })),
      })),
    );
  }

  public get leftbarConfig(): WritableSignal<Menu[]> {
    return this.#leftbarConfig;
  }

  public set leftbarConfig(value: Menu[]) {
    this.#leftbarConfig.set(value);
  }

  public get appIdentity(): Signal<AppIdentity> {
    return this.#appIdentity;
  }

  public get helpMenu(): WritableSignal<MenuItemType[]> {
    return this.#helpMenu;
  }
}
